import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Popover } from 'radix-ui';
import { toast } from 'sonner';
import { Bell, Briefcase, CheckCheck, ListChecks, MessageSquare, Star, Users, Wallet } from 'lucide-react';
import { useInboxCounts, useMarkRead, useNotifications } from '@/api/notifications';
import { useUserLive } from '@/api/live';
import type { NotificationDto, NotificationGroup } from '@/api/types';
import { formatDateTime, formatMoney, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

export function groupOf(kind: string): NotificationGroup {
  const prefix = kind.split('.')[0];
  return (prefix === 'job' ? 'jobs' : prefix === 'team' ? 'teams' : prefix) as NotificationGroup;
}

const GROUP_ICON: Record<NotificationGroup, ReactNode> = {
  jobs: <Briefcase />, work: <ListChecks />, teams: <Users />, payments: <Wallet />, reviews: <Star />, messages: <MessageSquare />,
};

/** The i18n key of a notification: its kind, plus a variant when the sentence depends on the data (same rules as the e-mails). */
export function notificationKey(n: NotificationDto): string {
  const base = n.kind.replace(/\./g, '_');
  switch (n.kind) {
    case 'job.application_received':
    case 'job.invited':
      return n.data.team ? `${base}_team` : base;
    case 'job.invite_answered':
    case 'team.offer_answered':
      return `${base}_${n.data.accepted === 'false' ? 'false' : 'true'}`;
    case 'account.membership_trial_ending':
    case 'account.membership_payment_failed':
    case 'account.membership_expired':
      // An organization's managers hear about the organization; a tester about their own membership.
      return n.data.org ? `${base}_org` : base;
    case 'work.run_reviewed':
    case 'reviews.dispute_resolved':
      return `${base}_${n.data.decision ?? n.data.outcome ?? 'Accepted'}`;
    case 'work.assigned':
      return n.data.case ? `${base}_case` : base;
    case 'payments.refund_decided':
      return `${base}_${n.data.outcome === 'Rejected' ? 'Rejected' : 'Sent'}`;
    default:
      return base;
  }
}

/** One localized sentence for a notification. */
export function notificationText(n: NotificationDto, t: TFunction, lang: string): string {
  const d = n.data;
  const values: Record<string, unknown> = { ...d };
  if (d.amount) values.amount = formatMoney(Number(d.amount), lang);
  if (d.total) values.total = formatMoney(Number(d.total), lang);
  if (d.due) values.due = formatDateTime(d.due, lang);
  if (d.status) values.status = t(`bugStatus.${d.status}`, { defaultValue: d.status });
  if (d.count) values.count = Number(d.count);
  if (d.rating) values.rating = Number(d.rating);
  return t(`notifications.kind.${notificationKey(n)}`, { ...values, defaultValue: n.kind });
}

export function NotificationIcon({ kind, className }: { kind: string; className?: string }) {
  return (
    <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-3 text-ink-2 [&>svg]:size-4', className)} aria-hidden>
      {GROUP_ICON[groupOf(kind)] ?? <Bell />}
    </span>
  );
}

/** A notification row: opening it marks it read and goes to where it points. */
export function NotificationItem({ n, onOpen, compact }: { n: NotificationDto; onOpen?: () => void; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const markRead = useMarkRead();
  const unread = !n.readAt;
  const open = () => {
    if (unread) markRead.mutate({ ids: [n.id] });
    onOpen?.();
    navigate(n.link);
  };
  return (
    <li>
      <button
        type="button"
        onClick={open}
        className={cn('flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-3', compact ? 'text-sm' : 'text-sm sm:px-4')}
      >
        <NotificationIcon kind={n.kind} className={unread ? 'bg-accent-soft text-accent-text' : undefined} />
        <span className="min-w-0 flex-1">
          <span className={cn('block', unread ? 'font-medium text-ink' : 'text-ink-2')}>{notificationText(n, t, i18n.language)}</span>
          {n.data.note && <span className="mt-0.5 block truncate text-xs text-ink-3">“{n.data.note}”</span>}
          <span className="mt-0.5 block text-[11px] text-ink-3">{formatRelative(n.createdAt, i18n.language)}</span>
        </span>
        {unread && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" aria-label={t('notifications.unread')} />}
      </button>
    </li>
  );
}

/** The bell in the shell: unread count, the latest notifications, live toasts for new ones. */
export function NotificationBell({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const counts = useInboxCounts();
  const latest = useNotifications(false, 8);
  const markRead = useMarkRead();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useUserLive(true, (n) => {
    // No toast for a message in the conversation that is already open.
    if (n.kind === 'messages.received' && `${location.pathname}${location.search}` === n.link) return;
    toast(notificationText(n, t, i18n.language), { action: { label: t('notifications.open'), onClick: () => navigate(n.link) } });
  });
  const unread = counts.data?.notifications ?? 0;
  const items = latest.data?.pages.flatMap((p) => p.items) ?? [];
  const label = unread > 0 ? t('notifications.bellUnread', { count: unread }) : t('notifications.bell');
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className={cn('relative flex size-8 items-center justify-center rounded-[var(--radius-ctl)] text-ink-2 hover:bg-surface-3 hover:text-ink', className)}
        >
          <Bell className="size-[18px]" />
          {unread > 0 && (
            <span data-unread-count="" className="mono absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-suspicious px-1 text-[10px] font-semibold leading-none text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={12}
          aria-label={t('notifications.title')}
          className="z-50 flex max-h-[min(32rem,80dvh)] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-pop)]"
        >
          <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">{t('notifications.title')}</h2>
            <button
              type="button"
              disabled={unread === 0 || markRead.isPending}
              onClick={() => markRead.mutate({ all: true })}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent-text hover:underline disabled:text-ink-3 disabled:no-underline"
            >
              <CheckCheck className="size-3.5" />{t('notifications.markAll')}
            </button>
          </div>
          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-ink-3">{latest.isPending ? t('common.loading') : t('notifications.empty')}</p>
          ) : (
            <ul className="divide-y divide-line overflow-y-auto">
              {items.map((n) => <NotificationItem key={n.id} n={n} compact onOpen={() => setOpen(false)} />)}
            </ul>
          )}
          <Link to="/notifications" onClick={() => setOpen(false)} className="border-t border-line px-3 py-2 text-center text-sm font-medium text-accent-text hover:bg-surface-3">
            {t('notifications.seeAll')}
          </Link>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The inbox link in the shell, with the number of conversations that have unread messages. */
export function MessagesLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  const counts = useInboxCounts();
  const unread = counts.data?.messages ?? 0;
  const label = unread > 0 ? t('messages.linkUnread', { count: unread }) : t('messages.title');
  return (
    <Link to="/messages" aria-label={label} title={label}
      className={cn('relative flex size-8 items-center justify-center rounded-[var(--radius-ctl)] text-ink-2 hover:bg-surface-3 hover:text-ink', className)}>
      <MessageSquare className="size-[18px]" />
      {unread > 0 && (
        <span className="mono absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-accent-ink">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}
